/**
 * Lesson Engine — Schema-driven state machine for guided lessons.
 *
 * States: LOADING → INTRO → STEP_EXECUTING → STEP_WAITING → STEP_TRANSITION → FREEFORM → SUMMARY
 *
 * Accepts lesson JSON (or URL to fetch from R2), validates schema,
 * checks required datasets, executes globe actions via query router,
 * manages narration, handles question scoring, tracks results.
 */
import { validateLesson } from './lesson-schema.js'
import catalog from '../data/catalog.js'
import { TIMEOUT_LESSON_FETCH } from '../config.js'
import { evaluateMapClick, getMapClickAttempts } from './map-click.js'

// ── Open-Response Grading (CL-139, roadmap H1.2) ─────────────

/** Verdicts the AI grader may return; anything else resolves to 'review'. */
export const GRADE_VERDICTS = ['correct', 'partial', 'incorrect']

/** Student-facing fail-closed message (mirrors the Worker's /api/grade). */
export const GRADE_REVIEW_FEEDBACK = 'Your answer has been saved for your teacher to review.'

// ── Adaptive Branching (CL-150, roadmap H2.3) ────────────────

/**
 * Runtime loop protection: no step may be *entered* more than this many
 * times. An explicit on_pass/on_fail target whose budget is exhausted
 * fails open to sequential advance — no authored JSON can trap a student
 * in a remediation loop. Not author-configurable in v1 by design.
 */
export const MAX_VISITS_PER_STEP = 2

/**
 * Pure — collect the expected points to grade an open_response answer
 * against, from whichever field the lesson question carries.
 * @param {Object} q - The question object
 * @returns {string[]} Expected points (may be empty)
 */
export function expectedPointsForQuestion(q) {
  if (!q || typeof q !== 'object') return []
  if (Array.isArray(q.key_points) && q.key_points.length > 0) {
    return q.key_points.filter((p) => typeof p === 'string' && p.trim()).map((p) => p.trim())
  }
  if (typeof q.grading_rubric === 'string' && q.grading_rubric.trim()) {
    return [q.grading_rubric.trim()]
  }
  if (typeof q.correct_answer === 'string' && q.correct_answer.trim()) {
    return [q.correct_answer.trim()]
  }
  return []
}

// ── Engine States ────────────────────────────────────────────

export const LessonState = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  INTRO: 'INTRO',
  STEP_EXECUTING: 'STEP_EXECUTING',
  STEP_WAITING: 'STEP_WAITING',
  STEP_TRANSITION: 'STEP_TRANSITION',
  FREEFORM: 'FREEFORM',
  SUMMARY: 'SUMMARY',
}

// ── Event Emitter Mixin ──────────────────────────────────────

class EventEmitter {
  constructor() {
    this._listeners = {}
  }

  on(event, fn) {
    ;(this._listeners[event] ||= []).push(fn)
    return () => this.off(event, fn)
  }

  off(event, fn) {
    const list = this._listeners[event]
    if (list) this._listeners[event] = list.filter((f) => f !== fn)
  }

  emit(event, data) {
    ;(this._listeners[event] || []).forEach((fn) => {
      try {
        fn(data)
      } catch (e) {
        console.error(`[LessonEngine] Event handler error (${event}):`, e)
      }
    })
  }
}

// ── Lesson Engine ────────────────────────────────────────────

class LessonEngine extends EventEmitter {
  constructor() {
    super()

    /** @type {import('./lesson-schema.js').Lesson|null} */
    this.lesson = null
    this.state = LessonState.IDLE
    this.stepIndex = -1
    this.score = 0
    this.maxScore = 0
    this.startTime = 0
    this.stepResults = []
    this._freeformTimer = null
    this._freeformRemaining = 0
    this._unregisterMapClick = null
    this._mapClickAttemptsLeft = 0
    // CL-150 (H2.3): branching state. _path = visited step indices (drives
    // previous() and the summary path); _visits = entry count per step id
    // (loop budget); _answeredThisVisit = the result submitted during the
    // CURRENT visit only — a stale result from an earlier visit must never
    // drive a branch decision.
    this._path = []
    this._visits = new Map()
    this._answeredThisVisit = null

    // External dependencies — set by the consumer
    /** @type {Function|null} route(intent) */
    this.routeIntent = null
    /** @type {Function|null} parseIntent(command) */
    this.parseIntent = null
    /** @type {Function|null} speak(text) */
    this.speak = null
    /**
     * @type {Function|null} gradeOpenResponse(question, answer, opts)
     * opts = { expectedPoints: string[], gradeBand: string|null }.
     * Must resolve to { verdict: 'correct'|'partial'|'incorrect'|'review',
     * feedback? } — main.js wires it to POST /api/grade (CL-139). Anything
     * else (throw, malformed verdict) fails closed to 'review'.
     */
    this.gradeOpenResponse = null
    /**
     * @type {Function|null} registerMapClick(handler) → unregister
     * handler receives the resolved clicked feature ({ name, iso, continent }
     * or null for ocean clicks). Injected by the consumer (main.js) so the
     * engine never touches the globe directly.
     */
    this.registerMapClick = null
    /**
     * @type {Function|null} reportStepResult(stepResult) — CL-146 (roadmap
     * H2.2): buffers one row per scored step for D1 persistence. Called
     * from the same seam that updates scoring (submitAnswer), so every
     * question type — including map_click attempts-exhausted and 'review'
     * verdicts — is reported. main.js wires it to
     * src/lessons/result-reporter.js; fail-soft and fire-and-forget by
     * contract (a reporter failure must never break a lesson).
     */
    this.reportStepResult = null
    /**
     * @type {Function|null} flushResults(lessonId) — CL-146: POSTs the
     * buffered step rows. Called on lesson finish AND early stop. Async but
     * never awaited — persistence must never block the lesson flow.
     */
    this.flushResults = null
  }

  /** Current lesson step (or null) */
  get currentStep() {
    if (!this.lesson || this.stepIndex < 0 || this.stepIndex >= this.lesson.steps.length)
      return null
    return this.lesson.steps[this.stepIndex]
  }

  /** Total number of steps */
  get totalSteps() {
    return this.lesson?.steps?.length || 0
  }

  /** Whether the engine is running a lesson */
  get isActive() {
    return this.state !== LessonState.IDLE
  }

  /** Elapsed time in seconds */
  get elapsedSeconds() {
    if (!this.startTime) return 0
    return Math.round((Date.now() - this.startTime) / 1000)
  }

  // ── Loading ────────────────────────────────────────────────

  /**
   * Load and start a lesson from a URL or JSON object.
   * @param {string|Object} source - URL string or lesson JSON object
   */
  async load(source) {
    this._setState(LessonState.LOADING)

    try {
      let lessonData

      if (typeof source === 'string') {
        // Fetch from URL (R2 or local)
        const resp = await fetch(source, { signal: AbortSignal.timeout(TIMEOUT_LESSON_FETCH) })
        if (!resp.ok) throw new Error(`Failed to fetch lesson: ${resp.status} ${resp.statusText}`)
        lessonData = await resp.json()
      } else {
        lessonData = source
      }

      // Validate schema
      const errors = validateLesson(lessonData)
      if (errors.length > 0) {
        throw new Error(`Invalid lesson schema:\n${errors.join('\n')}`)
      }

      // Check required datasets
      if (lessonData.required_datasets?.length > 0) {
        const missing = lessonData.required_datasets.filter((ds) => !catalog.resolveDataset(ds))
        if (missing.length > 0) {
          console.warn(
            `[LessonEngine] Missing datasets: ${missing.join(', ')} — lesson may not work fully`
          )
        }
      }

      this.lesson = lessonData
      this.stepIndex = -1
      this.score = 0
      this.maxScore = this._computeMaxScore()
      this.startTime = Date.now()
      this.stepResults = []
      this._path = []
      this._visits = new Map()
      this._answeredThisVisit = null

      this.emit('lesson-started', {
        id: this.lesson.id,
        title: this.lesson.title,
        totalSteps: this.totalSteps,
      })

      // Show intro then advance to first step
      this._setState(LessonState.INTRO)
    } catch (err) {
      console.error('[LessonEngine] Load failed:', err)
      this._setState(LessonState.IDLE)
      throw err
    }
  }

  // ── Navigation ─────────────────────────────────────────────

  /** Start from the intro (advance to the first non-remediation step). */
  begin() {
    if (this.state !== LessonState.INTRO) return
    // CL-150: a lesson that (pathologically) opens with remediation steps
    // starts at the first sequential step instead of index 0.
    this._advanceTo(this._nextSequentialIndex(-1))
  }

  /** Advance to the next step (branch-aware since CL-150). */
  async next() {
    if (this.state === LessonState.INTRO) {
      this.begin()
      return
    }

    if (this.state !== LessonState.STEP_WAITING && this.state !== LessonState.FREEFORM) return

    this._advanceTo(this._resolveNextIndex())
  }

  /** Go back to the previously VISITED step (path history, not index−1). */
  previous() {
    if (this.state !== LessonState.STEP_WAITING && this.state !== LessonState.FREEFORM) return
    if (this._path.length < 2) return

    // Undo the visit bookkeeping for the step we're leaving, and for the one
    // we're returning to (its _executeStep will re-increment) — back/forward
    // navigation must never burn branch budget (CL-150).
    const leavingId = this.currentStep?.id
    if (leavingId) this._decrementVisit(leavingId)
    this._path.pop()
    const prevIndex = this._path.pop()
    const returningId = this.lesson?.steps?.[prevIndex]?.id
    if (returningId) this._decrementVisit(returningId)

    this.stepIndex = prevIndex
    this._executeStep()
  }

  // ── Branch Resolution (CL-150, roadmap H2.3) ───────────────

  /** Move to a resolved step index, finishing the lesson if past the end. */
  _advanceTo(index) {
    if (index >= this.totalSteps) {
      this._finish()
    } else {
      this.stepIndex = index
      this._executeStep()
    }
  }

  /**
   * Resolve where next() should go, honoring on_pass/on_fail (CL-149 schema).
   * Verdict → transition: correct/partial → on_pass; incorrect → on_fail;
   * 'review' NEVER branches (fail-closed grading stays neutral, CL-139).
   * Any bad target or exhausted visit budget fails open to sequential next.
   */
  _resolveNextIndex() {
    const step = this.currentStep
    if (!step) return this.totalSteps

    let target = 'next'
    const result = this._answeredThisVisit
    if (step.question && result && result.stepId === step.id) {
      if (result.verdict === 'correct' || result.verdict === 'partial') {
        target = step.on_pass || 'next'
      } else if (result.verdict === 'incorrect') {
        target = step.on_fail || 'next'
      }
      // verdict 'review' → keep 'next'
    } else if (!step.question && step.on_pass) {
      // Unconditional goto on a question-less step
      target = step.on_pass
    }

    if (target === 'end') return this.totalSteps
    if (target === 'next') return this._nextSequentialIndex(this.stepIndex)

    const idx = this.lesson.steps.findIndex((s) => s.id === target)
    if (idx === -1 || !this._visitBudgetOk(idx)) {
      // Fail open (design principle: a broken branch never strands a student)
      return this._nextSequentialIndex(this.stepIndex)
    }
    return idx
  }

  /** Smallest index > from whose step is not remediation-type (skip blocks). */
  _nextSequentialIndex(from) {
    let j = from + 1
    while (j < this.totalSteps && this.lesson?.steps?.[j]?.type === 'remediation') j++
    return j
  }

  _visitBudgetOk(index) {
    const id = this.lesson?.steps?.[index]?.id
    if (!id) return false
    return (this._visits.get(id) || 0) < MAX_VISITS_PER_STEP
  }

  _decrementVisit(stepId) {
    this._visits.set(stepId, Math.max(0, (this._visits.get(stepId) || 0) - 1))
  }

  /** Stop the lesson early. */
  stop() {
    this._clearFreeformTimer()
    this._disarmMapClick()
    // CL-146: an early stop still persists whatever was already scored
    // (fire-and-forget — must never delay teardown). Capture the id before
    // the lesson reference is dropped.
    this._flushResultsSafe(this.lesson?.id)
    this._setState(LessonState.IDLE)
    this.lesson = null
  }

  /** Retry the lesson from the beginning. */
  retry() {
    if (!this.lesson) return
    const lesson = this.lesson
    this.stop()
    this.load(lesson)
  }

  // ── Step Execution ─────────────────────────────────────────

  async _executeStep() {
    const step = this.currentStep
    if (!step) return

    this._clearFreeformTimer()
    this._disarmMapClick() // leaving the previous step must never leak a click handler
    this._setState(LessonState.STEP_EXECUTING)

    // CL-150: visit bookkeeping — path history + per-step entry count (loop
    // budget) + fresh answer slot for this visit.
    this._path.push(this.stepIndex)
    if (step.id) this._visits.set(step.id, (this._visits.get(step.id) || 0) + 1)
    this._answeredThisVisit = null

    this.emit('step-changed', {
      index: this.stepIndex,
      total: this.totalSteps,
      step,
      // CL-150: progress along the student's own path (branch-aware), and a
      // flag so the panel can frame remediation supportively.
      pathLength: this._path.length,
      isRemediation: step.type === 'remediation',
    })

    // Handle narration timing: before_action
    const timing = step.narration?.timing || 'before_action'
    if (step.narration && timing === 'before_action') {
      await this._narrate(step.narration)
    }

    // Execute globe action
    if (step.globe_action) {
      if (step.narration && timing === 'with_action') {
        // Narrate and act concurrently
        await Promise.all([
          this._narrate(step.narration),
          this._executeGlobeAction(step.globe_action),
        ])
      } else {
        await this._executeGlobeAction(step.globe_action)
      }
    }

    // Handle narration timing: after_action
    if (step.narration && timing === 'after_action') {
      await this._narrate(step.narration)
    }

    // Transition to the right waiting state
    if (step.type === 'freeform') {
      this._enterFreeform(step)
    } else if (step.auto_advance && !step.question) {
      this._setState(LessonState.STEP_TRANSITION)
      const delay = step.auto_advance_delay_ms || 3000
      setTimeout(() => {
        if (this.state === LessonState.STEP_TRANSITION) {
          this.next()
        }
      }, delay)
    } else {
      this._setState(LessonState.STEP_WAITING)
      if (step.question?.type === 'map_click') {
        this._armMapClick(step)
      }
    }
  }

  async _executeGlobeAction(action) {
    if (!action) return

    const delay = action.delay_ms || 0
    if (delay > 0) await this._wait(delay)

    try {
      if (action.intent) {
        // Pre-built intent — route directly
        await this.routeIntent?.(action.intent)
      } else if (action.command) {
        // Natural language command — parse then route
        const intent = await this.parseIntent?.(action.command)
        if (intent) {
          await this.routeIntent?.(intent)
        }
      }
    } catch (err) {
      console.warn('[LessonEngine] Globe action failed:', err.message)
    }
  }

  async _narrate(narration) {
    if (!narration) return
    const speakText = narration.speak || narration.text
    if (speakText && this.speak) {
      await this.speak(speakText)
    }
  }

  // ── Freeform Mode ──────────────────────────────────────────

  _enterFreeform(step) {
    this._setState(LessonState.FREEFORM)

    const duration = step.freeform?.duration_seconds || 120
    this._freeformRemaining = duration

    this._freeformTimer = setInterval(() => {
      this._freeformRemaining--
      this.emit('freeform-tick', { remaining: this._freeformRemaining })

      if (this._freeformRemaining <= 0) {
        this._clearFreeformTimer()
        this.next()
      }
    }, 1000)
  }

  _clearFreeformTimer() {
    if (this._freeformTimer) {
      clearInterval(this._freeformTimer)
      this._freeformTimer = null
    }
    this._freeformRemaining = 0
  }

  // ── Question Scoring ───────────────────────────────────────

  /**
   * Submit an answer for the current step's question.
   * @param {string|number} answer - The student's answer
   * @returns {Promise<Object>} Scoring result { correct, score, explanation, feedback }
   */
  async submitAnswer(answer) {
    const step = this.currentStep
    if (!step?.question) return null

    const q = step.question
    const points = q.points || 1
    let correct = false
    let feedback = ''
    // Verdict model (CL-139): 'correct' | 'partial' | 'incorrect' for scored
    // outcomes, 'review' for the neutral fail-closed outcome (open_response
    // grading failures). Non-AI question types derive it from `correct`.
    let verdict = null

    switch (q.type) {
      case 'multiple_choice':
      case 'true_false':
      case 'fill_blank':
        correct =
          String(answer).toLowerCase().trim() === String(q.correct_answer).toLowerCase().trim()
        feedback = correct ? 'Correct!' : `Incorrect. The answer is: ${q.correct_answer}`
        break

      case 'numeric': {
        const numAnswer = Number(answer)
        const tolerance = q.tolerance || 0
        correct = !isNaN(numAnswer) && Math.abs(numAnswer - q.correct_answer) <= tolerance
        feedback = correct
          ? 'Correct!'
          : `Incorrect. The answer is ${q.correct_answer}${tolerance > 0 ? ` (±${tolerance})` : ''}.`
        break
      }

      case 'open_response': {
        // Fail-closed AI grading (CL-139, roadmap H1.2). The old path sent
        // the answer through /api/intent and regex-scraped the reply,
        // treating every failure as correct. Now: any failure — no grader
        // wired, network error, malformed verdict — resolves to the neutral
        // 'review' verdict; an answer is never auto-marked correct.
        let graded = null
        if (this.gradeOpenResponse) {
          try {
            graded = await this.gradeOpenResponse(q.prompt, String(answer), {
              expectedPoints: expectedPointsForQuestion(q),
              gradeBand: this.lesson?.metadata?.grade_band || null,
            })
          } catch (err) {
            console.warn('[LessonEngine] AI grading failed (fail closed → review):', err.message)
          }
        }
        verdict = GRADE_VERDICTS.includes(graded?.verdict) ? graded.verdict : 'review'
        if (verdict === 'review') {
          feedback = GRADE_REVIEW_FEEDBACK
        } else {
          const defaults = {
            correct: 'Good answer!',
            partial: 'Partially correct — nice effort!',
            incorrect: 'Not quite.',
          }
          feedback =
            (typeof graded?.feedback === 'string' && graded.feedback.trim()) || defaults[verdict]
        }
        break
      }

      case 'map_click': {
        // answer is the resolved clicked feature ({ name, iso, continent } or null),
        // delivered by _handleMapClick after the retry loop resolves.
        const evaluation = evaluateMapClick(q, answer)
        correct = evaluation.correct
        const targetName = q.target?.name || 'the right place'
        feedback = correct
          ? 'Correct!'
          : `Not quite${evaluation.clickedName ? ` — that was ${evaluation.clickedName}` : ''}. The answer is ${targetName}.`
        break
      }

      default:
        feedback = 'Unknown question type.'
    }

    if (!verdict) verdict = correct ? 'correct' : 'incorrect'

    // Scoring (CL-139): full credit for 'correct', half credit for
    // 'partial', none for 'incorrect'. 'review' is NEUTRAL — 0 earned here,
    // and _finish() excludes its points from the effective max so a
    // fail-closed grading outcome never counts against the student.
    let scoreEarned = 0
    if (verdict === 'correct') scoreEarned = points
    else if (verdict === 'partial') scoreEarned = points / 2
    correct = verdict === 'correct' || verdict === 'partial'
    this.score += scoreEarned

    const result = {
      stepId: step.id,
      stepIndex: this.stepIndex,
      questionType: q.type,
      answer,
      correct,
      verdict,
      correctAnswer: q.correct_answer ?? q.target?.name,
      scoreEarned,
      maxPoints: points,
      explanation: q.explanation || '',
      feedback,
      // CL-150: which visit to this step produced this result (1-based).
      // Re-visited steps append a NEW row; summary scoring uses the latest
      // attempt per stepId (getSummary).
      attempt: this._visits.get(step.id) || 1,
      // CL-151: per-step concept resolution — step.concepts (CL-149 schema)
      // overrides the lesson-level standards/tags fallback, so mastery and
      // the dashboard heatmap get precise per-step tags.
      conceptTags:
        step.concepts ?? this.lesson?.metadata?.standards ?? this.lesson?.metadata?.tags ?? [],
      // CL-151: which transition edge this outcome selects — telemetry for
      // H2.4/H5.1 (deterministic from the verdict; 'review'/unknown → null).
      branchTaken:
        verdict === 'correct' || verdict === 'partial'
          ? 'pass'
          : verdict === 'incorrect'
            ? 'fail'
            : null,
    }

    this.stepResults.push(result)
    this._answeredThisVisit = result

    // CL-146 (roadmap H2.2): report the scored step through the injected
    // reporter seam — same place the score bookkeeping happens, so every
    // outcome (incl. map_click attempts-exhausted and 'review' verdicts)
    // is captured. Since CL-151 the result itself carries per-step
    // conceptTags + branchTaken. Fail-soft: reporter errors are ignored.
    if (this.reportStepResult) {
      try {
        this.reportStepResult({ ...result })
      } catch (err) {
        console.warn('[LessonEngine] Result reporting failed (ignored):', err.message)
      }
    }

    this.emit('question-answered', result)

    return result
  }

  // ── Map-Click Questions ────────────────────────────────────

  /**
   * Enter "awaiting map click" mode for a map_click step: register a globe
   * click resolver via the injected registerMapClick dependency and start
   * the attempts budget.
   */
  _armMapClick(step) {
    this._disarmMapClick()

    if (!this.registerMapClick) {
      console.warn(
        '[LessonEngine] map_click step but no registerMapClick dependency — clicks cannot be captured'
      )
      return
    }

    this._mapClickAttemptsLeft = getMapClickAttempts(step.question)
    this._unregisterMapClick = this.registerMapClick((clicked) => this._handleMapClick(clicked))

    this.emit('map-click-armed', {
      stepId: step.id,
      target: step.question.target,
      attemptsLeft: this._mapClickAttemptsLeft,
    })
  }

  /** Unregister the globe click resolver (no leaked handlers). */
  _disarmMapClick() {
    if (this._unregisterMapClick) {
      try {
        this._unregisterMapClick()
      } catch (err) {
        console.warn('[LessonEngine] map click unregister failed:', err.message)
      }
      this._unregisterMapClick = null
    }
    this._mapClickAttemptsLeft = 0
  }

  /**
   * Handle a resolved globe click while a map_click question is armed.
   * @param {{ name?: string, iso?: string, continent?: string }|null} clicked
   */
  async _handleMapClick(clicked) {
    const step = this.currentStep
    if (this.state !== LessonState.STEP_WAITING || step?.question?.type !== 'map_click') {
      this._disarmMapClick()
      return
    }

    const q = step.question
    const { correct, clickedName } = evaluateMapClick(q, clicked)

    if (correct) {
      this._disarmMapClick()
      await this.submitAnswer(clicked)
      return
    }

    // Ocean / unresolvable clicks don't burn an attempt (K-2 motor control).
    if (!clicked) {
      this._emitMapClickFeedback("I couldn't find a country there — try clicking on land!")
      return
    }

    this._mapClickAttemptsLeft--
    if (this._mapClickAttemptsLeft > 0) {
      this._emitMapClickFeedback(`That's ${clickedName} — try again!`)
      return
    }

    // Attempts exhausted — score as incorrect, then reveal the answer by
    // flying to it via the existing fast-query navigate path.
    this._disarmMapClick()
    await this.submitAnswer(clicked)
    if (q.target?.name) {
      this._executeGlobeAction({ command: `Go to ${q.target.name}` })
    }
  }

  _emitMapClickFeedback(message) {
    this.emit('map-click-feedback', {
      message,
      attemptsLeft: this._mapClickAttemptsLeft,
    })
    if (this.speak) {
      try {
        Promise.resolve(this.speak(message)).catch(() => {})
      } catch {
        /* speech is best-effort */
      }
    }
  }

  // ── Completion ─────────────────────────────────────────────

  /**
   * Build the lesson summary — the single source of truth for final scoring
   * (the panel consumes this too; do not duplicate the math elsewhere).
   *
   * CL-150 path-based scoring: only steps actually PRESENTED count toward the
   * effective max (an unvisited remediation step's points must never deflate
   * a passing student), and a re-visited step's LATEST attempt supersedes
   * earlier ones. CL-139 review-neutrality is preserved: 'review' rows earn 0
   * and are excluded from the effective max.
   *
   * @returns {Object|null} summary, or null when no lesson is loaded
   */
  getSummary() {
    if (!this.lesson) return null

    const latestByStep = new Map()
    for (const r of this.stepResults) latestByStep.set(r.stepId, r) // later rows overwrite
    const latestResults = [...latestByStep.values()]

    const reviewResults = latestResults.filter((r) => r.verdict === 'review')
    const reviewPoints = reviewResults.reduce((sum, r) => sum + (r.maxPoints || 0), 0)
    const presentedMax = latestResults.reduce((sum, r) => sum + (r.maxPoints || 0), 0)
    const effectiveMaxScore = Math.max(0, presentedMax - reviewPoints)
    const score = latestResults.reduce(
      (sum, r) => sum + (r.verdict === 'review' ? 0 : r.scoreEarned || 0),
      0
    )

    return {
      lessonId: this.lesson.id,
      title: this.lesson.title,
      totalSteps: this.totalSteps,
      completedSteps: this.stepIndex,
      score,
      maxScore: this.maxScore,
      effectiveMaxScore,
      reviewCount: reviewResults.length,
      percentage: effectiveMaxScore > 0 ? Math.round((score / effectiveMaxScore) * 100) : 100,
      passed:
        effectiveMaxScore > 0
          ? (score / effectiveMaxScore) * 100 >= (this.lesson.scoring?.passing_score || 60)
          : true,
      elapsedSeconds: this.elapsedSeconds,
      stepResults: this.stepResults,
      // CL-150: the student's actual route through the lesson (step ids).
      path: this._path.map((i) => this.lesson.steps[i]?.id).filter(Boolean),
    }
  }

  _finish() {
    this._clearFreeformTimer()
    this._disarmMapClick()

    const summary = this.getSummary()

    this._setState(LessonState.SUMMARY)
    this.emit('lesson-completed', summary)

    // CL-146: persist the step results now the lesson is complete.
    this._flushResultsSafe(this.lesson?.id)
  }

  /**
   * CL-146: fire-and-forget flush through the injected dependency. Sync
   * throws and async rejections are both swallowed — persistence is
   * fail-soft by contract and must never break lesson flow.
   */
  _flushResultsSafe(lessonId) {
    if (!this.flushResults || !lessonId) return
    try {
      Promise.resolve(this.flushResults(lessonId)).catch((err) => {
        console.warn('[LessonEngine] Result flush failed (ignored):', err?.message)
      })
    } catch (err) {
      console.warn('[LessonEngine] Result flush failed (ignored):', err.message)
    }
  }

  _computeMaxScore() {
    if (!this.lesson?.steps) return 0
    // CL-150: remediation steps are excluded — a perfect run never visits
    // them, so the advertised "N points" is what a perfect run can earn.
    return this.lesson.steps.reduce((sum, step) => {
      if (step.type === 'remediation') return sum
      return sum + (step.question?.points || (step.question ? 1 : 0))
    }, 0)
  }

  // ── State Management ───────────────────────────────────────

  _setState(newState) {
    const prev = this.state
    this.state = newState
    if (prev !== newState) {
      this.emit('state-changed', { from: prev, to: newState })
    }
  }

  _wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

export default LessonEngine
